import { BaseClientType, EditClientStepProps } from '../../../types';
import { Box, Button, IconButton, InputAdornment, TextField } from '@mui/material';
import { Controller, useFieldArray } from 'react-hook-form';
import { Icon } from '@iconify/react';
import { JSX } from 'react';
import { NaturalPerson } from '../../../../common/types/client';
import {
  validationRegExpPatterns,
  validationsErrorMessages,
} from '../../../../common/lib/validations';
import EditClientAddresses from '../../../components/editClientSteps/editClientAddresses';

/**
 * Natural person address details edit form
 */
const AddressDetailsStep = (props: EditClientStepProps<NaturalPerson>): JSX.Element => {
  const {
    fields: phoneFields,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({
    control: props.control,
    name: 'phones',
  });

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({
    control: props.control,
    name: 'emails',
  });

  return (
    <Box>
      <Box className="grid gap-8 md:grid-cols-2 mb-8">
        <Box className="space-y-4">
          {phoneFields.map((field, index) => (
            <Controller
              key={field.id}
              name={`phones.${index}.number`}
              control={props.control}
              rules={{
                required: { value: true, message: validationsErrorMessages.required },
                pattern: {
                  value: validationRegExpPatterns.mobilePhonePattern,
                  message: validationsErrorMessages.mobilePhone,
                },
              }}
              render={({ field, fieldState: { error } }): JSX.Element => (
                <TextField
                  {...field}
                  fullWidth
                  type="tel"
                  label="Mobile phone number *"
                  placeholder="e.g. 0831234567"
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    endAdornment:
                      index > 0 ? (
                        <InputAdornment position="end">
                          <IconButton edge="end" onClick={(): void => removePhone(index)}>
                            <Icon width={20} icon="ic:outline-delete" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                  }}
                />
              )}
            />
          ))}
          <Box>
            <Button
              size="small"
              variant="text"
              onClick={(): void =>
                appendPhone({ number: '', id: 0, roleplayerID: props.client.roleplayerID })
              }
              startIcon={<Icon icon="ic:twotone-plus" width={24} />}
            >
              Add another
            </Button>
          </Box>
        </Box>

        <Box className="space-y-4">
          {emailFields.map((field, index) => (
            <Controller
              key={field.id}
              name={`emails.${index}.address`}
              control={props.control}
              rules={{
                required: { value: true, message: validationsErrorMessages.required },
                pattern: {
                  value: validationRegExpPatterns.emailPattern,
                  message: validationsErrorMessages.email,
                },
              }}
              render={({ field, fieldState: { error } }): JSX.Element => (
                <TextField
                  {...field}
                  fullWidth
                  type="email"
                  label="Email address *"
                  placeholder="e.g. email@johndoe.com"
                  error={!!error}
                  helperText={error?.message}
                  InputProps={{
                    endAdornment:
                      index > 0 ? (
                        <InputAdornment position="end">
                          <IconButton edge="end" onClick={(): void => removeEmail(index)}>
                            <Icon width={20} icon="ic:outline-delete" />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                  }}
                />
              )}
            />
          ))}
          <Box>
            <Button
              size="small"
              variant="text"
              onClick={(): void =>
                appendEmail({ address: '', id: 0, roleplayerID: props.client.roleplayerID })
              }
              startIcon={<Icon icon="ic:twotone-plus" width={24} />}
            >
              Add another
            </Button>
          </Box>
        </Box>
      </Box>
      <EditClientAddresses {...(props as EditClientStepProps<BaseClientType>)} />
    </Box>
  );
};

export default AddressDetailsStep;
